/* eslint-disable no-param-reassign */
/* eslint-disable react/no-unescaped-entities */
import React, { useEffect, useState, useRef } from 'react';
import { Form, Input, notification, Popover, Spin } from 'antd';
import {
  EyeOutlined,
  EyeInvisibleOutlined,
  LockOutlined,
  CheckCircleFilled,
} from '@ant-design/icons';
import { resetPasswordSubmit } from '../services/auth';
import history from '../services/history';
import * as constants from '../utils/constants';
import { passwordPolicy } from '../utils/passwordPolicy';
import { useLocation } from 'react-router-dom';
import axios from 'axios';

const EMAIL_NOTIFICATION_URL =
  process.env.REACT_APP_EMAIL_NOTIFICATION_URL || 'http://localhost:3002';

const ResetPasswordForm = () => {
  const [passForm] = Form.useForm();
  const [validatingStatus, setValidatingStatus] = useState('');
  const [validateVisible, setValidateVisible] = useState(false);
  const [validatingStatusConfirm, setValidatingStatusConfirm] = useState('');
  const [newPassword, setNewPassword] = useState('');

  // Invite token states
  const [loading, setLoading] = useState(true);
  const [inviteValid, setInviteValid] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteError, setInviteError] = useState('');
  const [isInviteFlow, setIsInviteFlow] = useState(false);

  // OTP states
  const [otpSent, setOtpSent] = useState(false);
  const [otpCode, setOtpCode] = useState('');
  const [otpLoading, setOtpLoading] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);
  const [submitting, setSubmitting] = useState(false);

  // OTP input refs
  const otpRefs = useRef([]);

  const location = useLocation();

  // Check for invite token on mount
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const inviteToken = params.get('invite');

    if (inviteToken) {
      setIsInviteFlow(true);
      verifyInviteToken(inviteToken);
    } else {
      setLoading(false);
      setIsInviteFlow(false);
    }
  }, [location.search]);

  // Resend timer countdown
  useEffect(() => {
    if (resendTimer > 0) {
      const timer = setTimeout(() => setResendTimer(resendTimer - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendTimer]);

  const verifyInviteToken = async (token) => {
    try {
      const response = await axios.get(`${EMAIL_NOTIFICATION_URL}/pih/verify-invite`, {
        params: { invite: token },
      });

      if (response.data?.data?.valid) {
        setInviteValid(true);
        setInviteEmail(response.data.data.payload?.email || '');
      } else {
        setInviteValid(false);
        setInviteError(response.data?.data?.error || 'Invalid or expired invitation link');
      }
    } catch (error) {
      console.error('Error verifying invite token:', error);
      setInviteValid(false);
      setInviteError('Unable to verify invitation link. Please try again or contact support.');
    } finally {
      setLoading(false);
    }
  };

  const sendOtp = async () => {
    if (!inviteEmail) {
      notification.error({ message: 'Email not found. Please try again.' });
      return;
    }

    setOtpLoading(true);
    try {
      await axios.post(`${EMAIL_NOTIFICATION_URL}/pih/send-otp`, {
        email: inviteEmail,
      });

      setOtpSent(true);
      setResendTimer(30);
      notification.success({ message: 'OTP sent to your email' });
    } catch (error) {
      console.error('Error sending OTP:', error);
      notification.error({ message: 'Failed to send OTP. Please try again.' });
    } finally {
      setOtpLoading(false);
    }
  };

  const handleSetPassword = async () => {
    try {
      const values = await passForm.validateFields();

      if (values.password !== values.password_confirm) {
        notification.warning({ message: 'Passwords do not match' });
        return;
      }

      if (!otpCode || otpCode.length !== 6) {
        notification.warning({ message: 'Please enter a valid 6-digit OTP' });
        return;
      }

      setSubmitting(true);

      const response = await axios.post(`${EMAIL_NOTIFICATION_URL}/pih/verify-otp-reset`, {
        email: inviteEmail,
        code: otpCode,
        password: values.password,
      });

      if (response.data?.data?.success) {
        notification.success({ message: 'Password set successfully! You can now log in.' });
        history.push('/login');
      } else {
        notification.error({ message: response.data?.data?.message || 'Failed to set password' });
      }
    } catch (error) {
      console.error('Error setting password:', error);
      const errorMsg = error.response?.data?.message || 'Failed to set password. Please try again.';
      notification.error({ message: errorMsg });
    } finally {
      setSubmitting(false);
    }
  };

  // Legacy reset password flow
  const changeTempPass = async (data) => {
    try {
      if (data.password === data.password_confirm) {
        if (data.lastName) {
          data.fullName = `${data.firstName} ${data.lastName}`;
        } else {
          data.fullName = `${data.firstName}`;
        }
        await resetPasswordSubmit(data.password, data.fullName);
        history.push('./login');
      } else {
        notification.warning({
          message: 'Passwords do not match',
        });
      }
    } catch (err) {
      notification.warning({
        message: 'Something went wrong.- Please try again!',
      });
    }
  };

  const validatePass = () => {
    passForm.validateFields().then(async (data) => {
      await changeTempPass(data);
    });
  };

  const validateToNextPassword = (e) => {
    const value = e.target.value;
    if (!value) {
      setValidateVisible(false);
    }
    if (value) {
      setNewPassword(value);
      setValidatingStatus('validating');
      setTimeout(() => {
        if (value.length > 7 && constants.PASSWORD_PATTERN.test(value)) {
          setValidatingStatus('success');
          setValidateVisible(false);
        } else {
          setValidatingStatus('warning');
          setValidateVisible(true);
        }
      }, 500);
    }
  };

  const validateToFirstPassword = (e) => {
    const value = e.target.value;
    if (value) {
      setValidatingStatusConfirm('validating');
      setTimeout(() => {
        if (value.length > 7 && value === newPassword) {
          setValidatingStatusConfirm('success');
        } else {
          setValidatingStatusConfirm('warning');
        }
      }, 500);
    }
  };

  // OTP input handlers
  const handleOtpChange = (index, value) => {
    const sanitizedValue = value.replace(/\D/, '');
    const newOtp = otpCode.split('');
    newOtp[index] = sanitizedValue;
    setOtpCode(newOtp.join(''));

    if (sanitizedValue && index < 5) {
      otpRefs.current[index + 1]?.focus();
    }
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otpCode[index] && index > 0) {
      otpRefs.current[index - 1]?.focus();
    }
  };

  const handleOtpPaste = (e) => {
    e.preventDefault();
    const pastedText = e.clipboardData.getData('text');
    const digits = pastedText.replace(/\D/g, '').slice(0, 6);
    setOtpCode(digits);
    const focusIndex = Math.min(digits.length, 5);
    setTimeout(() => {
      otpRefs.current[focusIndex]?.focus();
    }, 0);
  };

  // Loading state
  if (loading) {
    return (
      <div className="crce-reset-state-wrap">
        <div className="loading-state">
          <Spin size="large" />
          <p className="crce-reset-loading-text">Verifying invitation...</p>
        </div>
      </div>
    );
  }

  // Invalid/Expired invite link
  if (isInviteFlow && !inviteValid) {
    return (
      <div className="crce-reset-state-wrap">
        <div className="crce-login-header">
          <h1 className="crce-login-title">Invalid or Expired Link</h1>
          <p className="crce-login-subtitle">
            {inviteError ||
              'This invitation link is no longer valid. Please contact your administrator for a new invitation.'}
          </p>
        </div>
        <button
          type="button"
          className="crce-login-native-submit"
          onClick={() => history.push('/login')}
        >
          Go to Login
        </button>
        <p className="crce-login-footer">
          Pioneered by CRC Evans &copy;{new Date().getFullYear()}
          {' · '}
          <a href="https://www.crcevans.com/privacy-policy/" rel="noreferrer" target="_blank">
            Privacy Policy
          </a>
        </p>
      </div>
    );
  }

  // Valid invite flow - Set Password with OTP
  if (isInviteFlow && inviteValid) {
    return (
      <div className="crce-reset-state-wrap">
        <div className="crce-login-header">
          <LockOutlined className="crce-reset-lock-icon" />
          <h1 className="crce-login-title">Set Your Password</h1>
          <p className="crce-login-subtitle">Welcome to CRCE Connect</p>
        </div>
        <div className="crce-reset-email-display">
          <span className="crce-reset-email-text">{inviteEmail}</span>
          <CheckCircleFilled className="crce-reset-email-verified-icon" />
        </div>

        <Form name="set_password" form={passForm} layout="vertical">
          {/* Password Field */}
          <Popover
            placement="bottomRight"
            title={constants.TITLE}
            content={passwordPolicy}
            trigger="focus"
            visible={validateVisible}
          >
            <Form.Item
              name="password"
              rules={[
                { required: true, message: 'Please enter new password.' },
                {
                  pattern: constants.PASSWORD_PATTERN,
                  message: 'Password does not meet requirements',
                },
              ]}
              hasFeedback
              validateStatus={validatingStatus}
            >
              <Input.Password
                size="large"
                className="crce-login-input"
                placeholder="New password"
                onChange={validateToNextPassword}
                iconRender={(visible) =>
                  visible ? (
                    <EyeInvisibleOutlined className="crce-login-eye-icon" />
                  ) : (
                    <EyeOutlined className="crce-login-eye-icon" />
                  )
                }
              />
            </Form.Item>
          </Popover>

          {/* Confirm Password Field */}
          <Form.Item
            name="password_confirm"
            rules={[{ required: true, message: 'Please confirm your password.' }]}
            hasFeedback
            validateStatus={validatingStatusConfirm}
          >
            <Input.Password
              size="large"
              className="crce-login-input"
              placeholder="Confirm new password"
              onChange={validateToFirstPassword}
              iconRender={(visible) =>
                visible ? (
                  <EyeInvisibleOutlined className="crce-login-eye-icon" />
                ) : (
                  <EyeOutlined className="crce-login-eye-icon" />
                )
              }
            />
          </Form.Item>

          {/* OTP Section */}
          {!otpSent ? (
            <Form.Item>
              <button
                type="button"
                className="crce-login-native-submit"
                onClick={sendOtp}
                disabled={validatingStatus !== 'success' || validatingStatusConfirm !== 'success'}
              >
                {otpLoading ? 'Sending...' : 'Send Verification Code'}
              </button>
              <p className="crce-login-subtitle crce-reset-note">A 6-digit code will be sent to your email</p>
            </Form.Item>
          ) : (
            <div className="crce-otp-section">
              <div className="crce-reset-otp-header">
                <span className="crce-reset-otp-label">Enter Verification Code</span>
                <span className="crce-reset-otp-sent-badge">
                  <CheckCircleFilled /> Sent
                </span>
              </div>

              {/* OTP Input Boxes */}
              <div className="crce-otp-input-group">
                {[0, 1, 2, 3, 4, 5].map((index) => (
                  <input
                    key={index}
                    ref={(el) => (otpRefs.current[index] = el)}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={otpCode[index] || ''}
                    className={`crce-otp-box ${otpCode[index] ? 'filled' : ''}`}
                    onChange={(e) => handleOtpChange(index, e.target.value)}
                    onKeyDown={(e) => handleOtpKeyDown(index, e)}
                    onPaste={index === 0 ? handleOtpPaste : undefined}
                  />
                ))}
              </div>

              {/* Resend OTP */}
              <div className="crce-otp-resend">
                <span className="crce-otp-resend-text">Didn't receive code?</span>
                <button
                  type="button"
                  className={`crce-otp-resend-btn${resendTimer > 0 ? ' disabled' : ''}`}
                  disabled={resendTimer > 0}
                  onClick={sendOtp}
                >
                  {otpLoading ? 'Sending...' : resendTimer > 0 ? `Resend in ${resendTimer}s` : 'Resend OTP'}
                </button>
              </div>

              {/* Submit Button */}
              <Form.Item>
                <button
                  type="button"
                  className="crce-login-native-submit"
                  onClick={handleSetPassword}
                  disabled={otpCode.length !== 6 || submitting}
                >
                  {submitting ? 'Verifying...' : 'Verify & Set Password'}
                </button>
              </Form.Item>
            </div>
          )}
        </Form>

        <button
          type="button"
          className="crce-login-secondary-btn"
          onClick={() => history.push('/login')}
        >
          Back to Login
        </button>

        <p className="crce-login-footer">
          Pioneered by CRC Evans &copy;{new Date().getFullYear()}
          {' · '}
          <a href="https://www.crcevans.com/privacy-policy/" rel="noreferrer" target="_blank">
            Privacy Policy
          </a>
        </p>
      </div>
    );
  }

  // Legacy reset password form (no invite token)
  return (
    <div className="crce-reset-state-wrap">
      <div className="crce-login-header">
        <LockOutlined className="crce-reset-lock-icon" />
        <h1 className="crce-login-title">Change Password</h1>
        <p className="crce-login-subtitle">Set a new password to continue</p>
      </div>
      <Form name="normal_login" initialValues={{ remember: true }} form={passForm} layout="vertical">
        <Popover
          placement="bottomRight"
          title={constants.TITLE}
          content={passwordPolicy}
          trigger="focus"
          visible={validateVisible}
        >
          <Form.Item
            name="password"
            rules={[{ required: true, message: 'Please enter new password.' }]}
            hasFeedback
            validateStatus={validatingStatus}
          >
            <Input.Password
              size="large"
              className="crce-login-input"
              placeholder="New password"
              onChange={validateToNextPassword}
              iconRender={(visibility) =>
                visibility ? (
                  <EyeInvisibleOutlined className="crce-login-eye-icon" />
                ) : (
                  <EyeOutlined className="crce-login-eye-icon" />
                )
              }
            />
          </Form.Item>
        </Popover>
        <Form.Item
          name="password_confirm"
          rules={[{ required: true, message: 'Please confirm your password.' }]}
          hasFeedback
          validateStatus={validatingStatusConfirm}
        >
          <Input.Password
            size="large"
            className="crce-login-input"
            placeholder="Confirm new password"
            onChange={validateToFirstPassword}
            iconRender={(visibility) =>
              visibility ? (
                <EyeInvisibleOutlined className="crce-login-eye-icon" />
              ) : (
                <EyeOutlined className="crce-login-eye-icon" />
              )
            }
          />
        </Form.Item>

        <Form.Item>
          <button type="button" className="crce-login-native-submit" onClick={validatePass}>
            Change Password
          </button>
        </Form.Item>
        <button
          type="button"
          className="crce-login-secondary-btn"
          onClick={() => history.push('/login')}
        >
          Back to Login
        </button>
      </Form>
      <p className="crce-login-footer">
        Pioneered by CRC Evans &copy;{new Date().getFullYear()}
        {' · '}
        <a href="https://www.crcevans.com/privacy-policy/" rel="noreferrer" target="_blank">
          Privacy Policy
        </a>
      </p>
    </div>
  );
};

export default ResetPasswordForm;
